import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLocation } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { COMPANY } from '@/config';

export default function PrivacyPolicy() {
  const [ , navigate ] = useLocation();

  return (
    <div className="min-h-screen bg-[#0E1525]">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl border border-slate-700 bg-[#1E293B]">
            <CardHeader className="text-center space-y-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="h-4 w-4"/>
                  Back
                </Button>
                <div className="flex-1"/>
              </div>
              <CardTitle className="text-3xl font-bold text-white">
                Privacy Policy
              </CardTitle>
              <p className="text-sm text-gray-400">
                Last Updated: December 2024
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-6 text-gray-300">
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
                    <p>
                      This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our video chat service (&quot;Service&quot;). We are committed to protecting your privacy and being transparent about our data practices.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">2.1 Information You Provide</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>OAuth authentication data (from providers like Google, Facebook, VK)</li>
                      <li>Profile information (name, email, gender, date of birth)</li>
                      <li>Profile picture/avatar</li>
                      <li>User preferences and settings</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">2.2 Automatically Collected Information</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>IP address and approximate location (country level)</li>
                      <li>Browser type and version</li>
                      <li>Device information</li>
                      <li>Usage data and interactions with the Service</li>
                      <li>Session duration and connection metadata</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">2.3 Information We Do NOT Collect</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Video or audio content from your calls (peer-to-peer connections are not recorded)</li>
                      <li>Text chat messages (not stored on our servers)</li>
                      <li>Screenshots or recordings of your sessions</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
                    <p>We use the collected information for the following purposes:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Provide and maintain the Service</li>
                      <li>Authenticate users and manage accounts</li>
                      <li>Match users based on preferences (gender, location, age group)</li>
                      <li>Display user information during video chats (name, country flag)</li>
                      <li>Enforce our Terms of Service and community guidelines</li>
                      <li>Detect and prevent abuse, fraud, or illegal activities</li>
                      <li>Improve service quality and user experience</li>
                      <li>Communicate with users about service updates or issues</li>
                      <li>Comply with legal obligations</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">4. How We Share Your Information</h2>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">4.1 With Other Users</h3>
                    <p>When you use the Service, limited information is shared with matched users:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Your first name</li>
                      <li>Your country (via flag display)</li>
                      <li>Your profile picture (if provided)</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">4.2 With Third Parties</h3>
                    <p>We share information with:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>OAuth providers (Google, Facebook, VK) for authentication</li>
                      <li>Cloud infrastructure providers (for hosting and data storage)</li>
                      <li>Analytics providers (for service improvement)</li>
                      <li>Law enforcement (when required by law or to prevent harm)</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">4.3 What We Do NOT Share</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>We do not sell your personal information to third parties</li>
                      <li>We do not share your email address with other users</li>
                      <li>We do not provide your full profile to advertising networks</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">5. Data Security</h2>
                    <p>We implement security measures to protect your information:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Video calls use peer-to-peer WebRTC encryption (DTLS-SRTP)</li>
                      <li>All data transmission uses HTTPS/TLS encryption</li>
                      <li>Passwords and sensitive data are encrypted at rest</li>
                      <li>Access to user data is restricted to authorized personnel only</li>
                      <li>Regular security audits and updates</li>
                    </ul>
                    <p className="mt-2">
                      However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Account information is retained while your account is active</li>
                      <li>Session metadata may be retained for up to 90 days for abuse detection</li>
                      <li>Video and audio content is never stored (peer-to-peer connections only)</li>
                      <li>Deleted account data is removed within 30 days</li>
                      <li>Some data may be retained longer if required by law</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">7. Your Privacy Rights</h2>
                    <p>Depending on your location, you may have the following rights:</p>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">Access and Portability</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Request a copy of your personal data</li>
                      <li>Request data in a portable format</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">Correction and Deletion</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Update or correct your profile information</li>
                      <li>Request deletion of your account and data</li>
                    </ul>

                    <h3 className="text-lg font-medium text-gray-200 mb-2 mt-4">Control and Objection</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Opt out of certain data processing</li>
                      <li>Object to automated decision-making</li>
                      <li>Withdraw consent for data processing</li>
                    </ul>

                    <p className="mt-3">
                      To exercise these rights, contact us at {COMPANY.supportEmail}
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">8. Children&apos;s Privacy</h2>
                    <p>
                      Our Service is NOT intended for users under 18 years of age. We do not knowingly collect personal information from children. If we discover that a child under 18 has provided personal information, we will delete it immediately. If you believe a child has provided information to us, please contact us immediately.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">9. International Data Transfers</h2>
                    <p>
                      Your information may be transferred to and processed in countries other than your own. These countries may have different data protection laws. By using our Service, you consent to such transfers. We ensure appropriate safeguards are in place to protect your information during international transfers.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">10. Cookies and Tracking Technologies</h2>
                    <p>We use the following technologies:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li><strong>Essential Cookies:</strong> Required for authentication and session management</li>
                      <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service</li>
                      <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                    </ul>
                    <p className="mt-2">
                      You can control cookies through your browser settings, but disabling certain cookies may affect Service functionality.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">11. Third-Party Services</h2>
                    <p>Our Service integrates with third-party authentication providers:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Google OAuth - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Privacy Policy</a></li>
                      <li>Facebook Login - <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Facebook Privacy Policy</a></li>
                      <li>VK ID - <a href="https://vk.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">VK Privacy Policy</a></li>
                    </ul>
                    <p className="mt-2">
                      These providers have their own privacy policies. We are not responsible for their privacy practices.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">12. Changes to This Privacy Policy</h2>
                    <p>
                      We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated &quot;Last Updated&quot; date. Material changes will be communicated through the Service or via email. Continued use of the Service after changes constitutes acceptance of the updated Privacy Policy.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">13. California Privacy Rights (CCPA)</h2>
                    <p>California residents have additional rights under the California Consumer Privacy Act:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Right to know what personal information is collected</li>
                      <li>Right to know if personal information is sold or disclosed</li>
                      <li>Right to opt out of the sale of personal information (we do not sell data)</li>
                      <li>Right to request deletion of personal information</li>
                      <li>Right to non-discrimination for exercising privacy rights</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">14. European Privacy Rights (GDPR)</h2>
                    <p>If you are in the European Economic Area, you have additional rights under GDPR:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Right to access your personal data</li>
                      <li>Right to rectification of inaccurate data</li>
                      <li>Right to erasure (&quot;right to be forgotten&quot;)</li>
                      <li>Right to restrict processing</li>
                      <li>Right to data portability</li>
                      <li>Right to object to processing</li>
                      <li>Right to withdraw consent</li>
                      <li>Right to lodge a complaint with a supervisory authority</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">15. Contact Us</h2>
                    <p>If you have questions or concerns about this Privacy Policy or our data practices, contact us at:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Email: {COMPANY.supportEmail}</li>
                    </ul>
                  </section>

                  <div className="mt-8 p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-gray-300 font-medium">
                      By using our Service, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your information as described herein.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
